"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { sendSignInLinkToEmail } from "firebase/auth";
import { auth } from "@/lib/firebase/client";
import { Mail, ArrowLeft } from "lucide-react";
import NextLink from "next/link";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ReCAPTCHAV2 from "react-google-recaptcha";
import { sendMagicLinkAction } from "@/actions/snapsiteActions";

const loginSchema = z.object({
  email: z.string().email({ message: "Dirección de correo electrónico inválida." }),
});

type LoginFormInputs = z.infer<typeof loginSchema>;

export default function LoginForm() {
  const [loading, setLoading] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const { toast } = useToast();
  const { executeRecaptcha } = useGoogleReCaptcha();
  // Prellenado desde el correo "Tu SnapSite ya está listo" (creado por un admin desde
  // /admin/crear-snapsite) — la persona llega con ?email= para no tener que volver a
  // escribir el correo con el que se le registró. Sin efecto si no viene el parámetro.
  const searchParams = useSearchParams();
  const prefillEmail = searchParams.get("email") || "";
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: prefillEmail },
  });

  // Capa 1 (server, HTML propio vía Brevo) + Capa 2 (fallback nativo de Firebase si Brevo
  // no pudo entregar) — ver snapsite-magiclink-bienvenida-plan.md §4. sendMagicLinkAction
  // ya valida el reCAPTCHA por dentro (mismo criterio de siempre: sin token, deja pasar);
  // aquí no se vuelve a validar nada, solo se decide qué hacer con el resultado.
  const sendLink = async (email: string, recaptchaToken: string | null, isV2Challenge: boolean) => {
    const result = await sendMagicLinkAction(email, recaptchaToken, isV2Challenge);

    if (!result.ok) {
      if (result.requiresChallenge) {
        setPendingEmail(email);
        setShowChallenge(true);
        return;
      }
      toast({ variant: "destructive", title: "Verificación fallida", description: "No se pudo completar la verificación de seguridad. Intenta de nuevo." });
      return;
    }

    // El correo de nuestro dominio (Brevo) ya salió. Si no se pudo entregar, el respaldo
    // es el envío nativo de Firebase (feo, pero garantiza que la persona nunca se quede
    // sin poder entrar) — mismo `?email=` en la URL de destino en los dos casos, para que
    // FinishLoginClient.tsx pueda leerlo sin depender solo de localStorage.
    if (!result.delivered) {
      if (!auth) {
        toast({ variant: "destructive", title: "Error", description: "Servicio de autenticación no disponible." });
        return;
      }
      await sendSignInLinkToEmail(auth, email, {
        url: `${window.location.origin}/finish-login?email=${encodeURIComponent(email)}`,
        handleCodeInApp: true,
      });
    }

    window.localStorage.setItem("emailForSignIn", email);
    toast({
      title: "Revisa tu correo electrónico",
      description: `Se ha enviado un enlace de inicio de sesión a ${email}. Por favor, ábrelo para continuar.`,
    });
  };

  const onSubmit: SubmitHandler<LoginFormInputs> = async (data) => {
    setLoading(true);
    try {
      const token = executeRecaptcha ? await executeRecaptcha("sendMagicLink") : null;
      await sendLink(data.email, token, false);
    } catch (error: any) {
      console.error("Error enviando enlace de inicio de sesión:", error);
      toast({
        variant: "destructive",
        title: "Falló el Inicio de Sesión",
        description: error.message || "No se pudo enviar el enlace de inicio de sesión. Por favor, inténtalo de nuevo.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleV2Complete = async (v2Token: string | null) => {
    if (!v2Token || !pendingEmail) return;
    setShowChallenge(false);
    setLoading(true);
    try {
      await sendLink(pendingEmail, v2Token, true);
      setPendingEmail(null);
    } catch (error: any) {
      toast({ variant: "destructive", title: "Error", description: error.message || "Ocurrió un error." });
    } finally {
      setLoading(false);
    }
  };

  const v2SiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY;

  return (
    <>
    {showChallenge && v2SiteKey && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-card rounded-2xl shadow-2xl p-6 flex flex-col items-center gap-4 max-w-sm w-full mx-4">
          <p className="text-sm font-semibold text-center">Verifica que eres humano para continuar</p>
          <p className="text-xs text-muted-foreground text-center">
            Tu conexión generó una puntuación de seguridad baja. Completa el desafío para recibir tu enlace.
          </p>
          <ReCAPTCHAV2 sitekey={v2SiteKey} onChange={handleV2Complete} />
          <button
            type="button"
            onClick={() => { setShowChallenge(false); setPendingEmail(null); }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Cancelar
          </button>
        </div>
      </div>
    )}
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <NextLink href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" /> Volver al inicio
          </NextLink>
          <div className="flex justify-center mb-4">
            <img src="/logo_snapsite.svg" alt="Logo SnapSite" width={72} height={72} className="rounded-xl" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Iniciar sesión</h1>
          <p className="mt-2 text-muted-foreground">Crea tu increíble página de perfil en minutos.</p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 rounded-lg border bg-card p-8 shadow-lg">
          <div>
            <Label htmlFor="email" className="text-foreground">Dirección de Correo Electrónico</Label>
            <div className="mt-1">
              <Input
                id="email"
                type="email"
                autoComplete="email"
                {...register("email")}
                className="bg-card-foreground/5 placeholder:text-muted-foreground/80"
                placeholder="tu@ejemplo.com"
              />
              {errors.email && <p className="mt-1 text-sm text-destructive">{errors.email.message}</p>}
            </div>
          </div>

          <div>
            <p className="mb-3 text-center text-sm text-muted-foreground">
              ¡No se necesita contraseña! Te enviaremos un enlace mágico para iniciar sesión.
            </p>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                "Enviando Enlace..."
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" /> Enviar Enlace de Acceso
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
}
